import { useContext } from "react";
import DatePicker from "react-datepicker";
import cx from "classnames";
import isDate from "lodash/isDate";
import { ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";
import "react-datepicker/dist/react-datepicker.css";
import UserContext from "../../state/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";

export interface DatePresetOption {
  /** The display label for the preset option */
  label: string;
  /** Function that returns the date for this preset */
  getValue: () => Date;
}

interface DateInputProps {
  /** The onChange event, for handling state changes */
  onChange(newValue: Date | null): void;
  /** The current value for the input */
  value?: Date | null;
  /** The Id attribute for the input element, to match the Label */
  inputId?: string;
  /** The css class names to apply */
  className?: string;
  /** The Placeholder attribute value for the form element */
  placeholder?: string | undefined;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  /** Whether or not the field can be cleared */
  isClearable?: boolean;
  isReadOnly?: boolean;
  showTimeSelect?: boolean | undefined;
  allowDatesInPast?: boolean;
  allowDatesInFuture?: boolean;
  /** Whether to show the calendar icon in the input */
  showIcon?: boolean;
  /** Pre-selectable date options to display alongside the calendar */
  presetOptions?: DatePresetOption[];
}

/** A date input field, with common styling already applied
 */
const DateInput = ({
  onChange,
  showValidationErrors = false,
  validationResult = null,
  value = undefined,
  inputId = "",
  className = "block w-full border-0 bg-gray-100 rounded-md focus:outline-0 focus:ring-0",
  placeholder = undefined,
  isClearable = false,
  isReadOnly = false,
  showTimeSelect = false,
  allowDatesInPast = false,
  allowDatesInFuture = true,
  showIcon = false,
  presetOptions = [],
}: DateInputProps) => {
  const userContext = useContext(UserContext);

  const minDate = allowDatesInPast ? null : new Date();
  const maxDate = allowDatesInFuture ? null : new Date();

  const onValueChange = (newDate: Date | null) => {
    if (newDate && isDate(newDate)) {
      onChange(newDate);
    } else {
      onChange(null);
    }
  };

  const handlePresetSelect = (presetOption: DatePresetOption) => {
    const presetDate = presetOption.getValue();
    onChange(presetDate);
  };

  const timeFormat = showTimeSelect ? "h:mm aa" : "";

  return (
    <div className="datepicker-container rounded-md grow">
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}

      <div className={showIcon ? "relative" : ""}>
        {showIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-gray-400 h-4 w-4"
            />
          </div>
        )}
        <DatePicker
          id={inputId}
          className={cx(
            className,
            isReadOnly ? "cursor-not-allowed" : "",
            showIcon ? "pl-10" : "",
          )}
          showPopperArrow={false}
          calendarClassName="shadow-lg border border-gray-300"
          selected={value}
          placeholderText={placeholder}
          onChange={(date: Date) => onValueChange(date)}
          dateFormat={userContext.user.i18n.dateFormat + " " + timeFormat}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          calendarStartDay={1}
          isClearable={isClearable}
          disabled={isReadOnly}
          autoComplete="off"
          showTimeSelect={showTimeSelect}
          timeFormat={timeFormat}
          timeIntervals={30}
          timeCaption={"Time"}
          minDate={minDate}
          maxDate={maxDate}
          calendarContainer={({ children }) => (
            <div className="flex bg-white rounded-lg shadow-lg border border-gray-300 overflow-hidden">
              {/* Calendar */}
              <div className="flex-shrink-0">
                <div className="relative">{children}</div>
              </div>

              {/* Preset Options Sidebar */}
              {presetOptions.length > 0 && !isReadOnly && (
                <div className="hidden md:block w-48 bg-gray-50 border-l border-gray-200 p-3">
                  <div className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                    Quick Select
                  </div>
                  <div className="space-y-1">
                    {presetOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handlePresetSelect(option)}
                        className="w-full text-left px-2 py-1.5 text-xs text-gray-700 rounded hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900 transition-colors"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default DateInput;
